import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  overlayClassName?: string;
  containerClassName?: string;
  ariaLabel?: string;
  closeOnOverlayClick?: boolean;
  showX?: boolean;
}

const DEFAULT_OVERLAY_CLASS = 'fixed inset-0 z-50 flex items-center justify-center bg-black/30';
const DEFAULT_CONTAINER_CLASS = 'bg-white rounded-lg shadow-lg w-full max-w-5xl relative p-0 max-h-screen overflow-y-auto mx-4 my-6';

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  children,
  overlayClassName = '',
  containerClassName = '',
  ariaLabel = 'Modal dialog',
  closeOnOverlayClick = true,
  showX = false,
}) => {
  const [maxHeight, setMaxHeight] = useState<string>('calc(100vh - 32px)');

  useEffect(() => {
    if (!open) return;

    // Preserve the underlying page's scroll position while the modal is open.
    // Setting body.overflow=hidden alone doesn't reliably keep the scroll
    // offset across open/close cycles, so we pin the body in place at its
    // current scrollY and then jump back to that scrollY when we unlock.
    // This pattern handles nested modals correctly because each open Modal
    // captures the position when *it* opens (which, for a nested modal, is
    // already the locked-at-top body position of the parent — a no-op shift).
    const scrollY = window.scrollY;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalRight = document.body.style.right;
    const originalWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    // Set maxHeight based on window.innerHeight
    const updateMaxHeight = () => {
      setMaxHeight(`${window.innerHeight - 32}px`); // 16px margin top and bottom
    };
    updateMaxHeight();
    window.addEventListener('resize', updateMaxHeight);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.right = originalRight;
      document.body.style.width = originalWidth;
      // Restore the scroll position the page was at before the modal opened.
      window.scrollTo(0, scrollY);
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`${DEFAULT_OVERLAY_CLASS} ${overlayClassName}`.trim()}
      onClick={closeOnOverlayClick ? onClose : undefined}
      aria-modal="true"
      role="dialog"
      aria-label={ariaLabel}
    >
      <div
        className={`${DEFAULT_CONTAINER_CLASS} ${containerClassName}`.trim()}
        style={{ maxHeight, marginTop: 16, marginBottom: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        {showX && (
          <div className="sticky top-0 z-10 bg-white bg-opacity-95 flex justify-end border-b border-gray-200 rounded-t-lg px-4 py-2">
            <button
              type="button"
              aria-label="Close modal"
              onClick={onClose}
              className="text-gray-700 text-2xl bg-white bg-opacity-80 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <FiX className="w-7 h-7" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};
